/**
 * Componente de imagen con lazy loading y placeholder
 *
 * Features:
 * - Lazy loading con Intersection Observer
 * - Skeleton placeholder mientras carga
 * - Fade-in suave al cargar
 * - Fallback automático si falla
 * - Soporte para thumbnails automáticos
 */

import React, { useState, useEffect, useRef } from 'react';
import { getThumbnailUrl, THUMBNAIL_SIZES } from '../utils/thumbnailUtils';
import './LazyImage.css';

function LazyImage({
  src,
  alt = '',
  className = '',
  thumbnailSize = THUMBNAIL_SIZES.THUMB,
  placeholder = null,
  onLoad,
  onError,
  style = {},
  ...props
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(null);
  const imgRef = useRef(null);

  // Intersection Observer para lazy loading
  useEffect(() => {
    if (!imgRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '50px', // Empezar a cargar 50px antes de que sea visible
      }
    );

    const currentImg = imgRef.current;
    observer.observe(currentImg);

    return () => {
      if (currentImg) {
        observer.unobserve(currentImg);
      }
    };
  }, []);

  // Cargar imagen cuando esté en viewport
  useEffect(() => {
    if (!isInView || !src) return;

    setIsLoading(true);
    setHasError(false);

    // Intentar cargar thumbnail primero
    const thumbnailUrl = getThumbnailUrl(src, thumbnailSize);
    const img = new Image();

    img.onload = () => {
      setCurrentSrc(thumbnailUrl);
      setIsLoading(false);
      if (onLoad) onLoad();
    };

    img.onerror = () => {
      console.log('[LazyImage] Thumbnail failed, trying original:', src);
      // Si el thumbnail falla, intentar con la original
      const originalImg = new Image();

      originalImg.onload = () => {
        setCurrentSrc(src);
        setIsLoading(false);
        if (onLoad) onLoad();
      };

      originalImg.onerror = () => {
        console.error('[LazyImage] Failed to load image:', src);
        setHasError(true);
        setIsLoading(false);
        if (onError) onError();
      };

      originalImg.src = src;
    };

    img.src = thumbnailUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [isInView, src, thumbnailSize, onLoad, onError]);

  // Renderizar placeholder mientras no está en viewport
  if (!isInView) {
    return (
      <div ref={imgRef} className={`lazy-image-container ${className}`} style={style}>
        {placeholder || <div className="lazy-image-skeleton" />}
      </div>
    );
  }

  // Renderizar skeleton mientras carga
  if (isLoading) {
    return (
      <div ref={imgRef} className={`lazy-image-container ${className}`} style={style}>
        {placeholder || <div className="lazy-image-skeleton pulse" />}
      </div>
    );
  }

  // Renderizar error si falló
  if (hasError) {
    return (
      <div
        ref={imgRef}
        className={`lazy-image-container lazy-image-error ${className}`}
        style={style}
      >
        <div className="lazy-image-error-content">
          <span className="material-icons">broken_image</span>
          <span className="error-text">Error al cargar</span>
        </div>
      </div>
    );
  }

  // Renderizar imagen cargada
  return (
    <img
      ref={imgRef}
      src={currentSrc}
      alt={alt}
      className={`lazy-image ${className} ${isLoading ? 'loading' : 'loaded'}`}
      style={style}
      {...props}
    />
  );
}

export default LazyImage;
